<template>
  <div class="dashboard">
    <!-- Selector de empresa (se muestra condicionalmente) -->
    <EmpresaSelector 
      v-if="showEmpresaSelector" 
      @empresa-selected="onEmpresaSelected"
    />
    
    <div class="dashboard-header">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1>Menú principal</h1>
        <div class="d-flex align-items-center gap-3">
          <!-- Caja de búsqueda del menú -->
          <div class="search-container-inline">
            <MenuSearchBox />
          </div>
          <div v-if="empresaSeleccionada" class="empresa-info">
            <small class="text-muted">Empresa actual:</small>
            <strong class="ms-1">{{ empresaSeleccionada.Nombre }}</strong>
            <small class="text-muted ms-2">(ID: {{ empresaSeleccionada.EmpresaId }})</small>
          </div>
        </div>
      </div>
    </div>
    
    <div class="dashboard-content">
      <div class="row">
        <!-- Tarjetas dinámicas basadas en el menú del usuario -->
        <template v-if="menuGroups.length > 0 || standaloneItems.length > 0">
          <!-- Secciones agrupadas por menú padre -->
          <div v-for="group in menuGroups" :key="group.parentId" class="menu-group mb-4 col-12">
            <h3 class="menu-section-title mb-3">{{ group.parentTitle }}</h3>
            <div class="row">
              <div class="col-md-4 mb-4" v-for="menuItem in group.children" :key="menuItem.MenuId">
                <div class="card menu-card">
                  <div class="card-body">
                    <h5 class="card-title">{{ menuItem.Titulo }}</h5>
                    <p class="card-text">{{ menuItem.Descripcion || 'Acceso a ' + menuItem.Titulo }}</p>
                    <div class="card-actions">
                      <router-link :to="formatRoute(menuItem.Enlace)" class="btn btn-link">Consultar {{ menuItem.Titulo.toLowerCase() }}</router-link>
                    </div>
                    <div class="card-icon">
                      <i :class="'bi bi-' + (menuItem.Imagen || 'grid')"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Elementos independientes (sin padre o sin hijos) al final -->
          <div v-if="standaloneItems.length > 0" class="menu-group mb-4 col-12">
            <h3 class="menu-section-title mb-3">Otras opciones</h3>
            <div class="row">
              <div class="col-md-4 mb-4" v-for="menuItem in standaloneItems" :key="menuItem.MenuId">
                <div class="card menu-card">
                  <div class="card-body">
                    <h5 class="card-title">{{ menuItem.Titulo }}</h5>
                    <p class="card-text">{{ menuItem.Descripcion || 'Acceso a ' + menuItem.Titulo }}</p>
                    <div class="card-actions">
                      <router-link :to="formatRoute(menuItem.Enlace)" class="btn btn-link">Consultar {{ menuItem.Titulo.toLowerCase() }}</router-link>
                    </div>
                    <div class="card-icon">
                      <i :class="'bi bi-' + (menuItem.Imagen || 'grid')"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        
        <!-- Tarjetas estáticas solo si no hay menú dinámico -->
        <template v-if="menuGroups.length === 0 && standaloneItems.length === 0">
          <!-- Mensaje informativo -->
          <div class="col-12 mb-4">
            <div class="alert alert-danger">
              <i class="bi bi-info-circle me-2"></i>
              Necesita iniciar sesión
            </div>
          </div>
          
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/shared/services/auth';
import EmpresaSelector from '@/shared/components/EmpresaSelector.vue';
import MenuSearchBox from '@/shared/components/MenuSearchBox.vue';
import { formatRoute } from '@/shared/utils/route-helper';
import { useAccessControl } from '@/composables/useAccessControl';

export default {
  name: 'DashboardView',
  setup() {
    // Variables de control de acceso - Dashboard requiere autenticación pero no permisos de menú
    const accessControl = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    });
    
    return {
      ...accessControl
    };
  },
  components: {
    EmpresaSelector,
    MenuSearchBox
  },
  data() {
    return {
      menuItems: [],
      menuGroups: [],
      standaloneItems: [],
      showEmpresaSelector: false,
      empresaSeleccionada: null
    };
  },
  created() {
    console.log('DashboardView creado, verificando selección de empresa...');
    this.checkEmpresaSelection();
  },
  
  methods: {
    formatRoute,
    checkEmpresaSelection() {
      // Verificar si el usuario tiene empresas activas
      if (authService.hasActiveEmpresas()) {
        // Verificar si ya tiene una empresa seleccionada
        const empresaSeleccionada = authService.getSelectedEmpresa();
        
        if (!empresaSeleccionada) {
          // Mostrar selector de empresa
          this.showEmpresaSelector = true;
        } else {
          // Ya tiene empresa seleccionada, continuar normalmente
          this.empresaSeleccionada = empresaSeleccionada;
          this.loadMenuItems();
        }
      } else {
        // No tiene empresas activas, continuar sin selección
        this.loadMenuItems();
      }
    },
    
    onEmpresaSelected(empresa) {
      console.log('Empresa seleccionada en el dashboard:', empresa);
      this.empresaSeleccionada = empresa;
      this.showEmpresaSelector = false;
      
      // Cargar el menú después de seleccionar la empresa
      this.loadMenuItems();
    },
    
    loadMenuItems() {
      console.log('Cargando elementos del menú para el dashboard...');
      const userMenu = authService.getUserMenu();
      
      if (userMenu && Array.isArray(userMenu)) {
        console.log('Menú encontrado:', userMenu);
        
        // Filtrar solo elementos activos
        this.menuItems = userMenu.filter(item => item.EsActivo === true);
        
        // Identificar elementos padre (que tienen hijos)
        const parentMenuIds = new Set();
        const parentMenuItems = {};
        
        // Primero identificamos todos los IDs de padres mencionados
        this.menuItems.forEach(item => {
          if (item.MenuIdPadre && item.MenuIdPadre > 0) {
            parentMenuIds.add(item.MenuIdPadre);
          }
        });
        
        console.log('IDs de menús padre:', [...parentMenuIds]);
        
        // Crear grupos de menú basados en la relación padre-hijo
        this.menuGroups = [];
        this.standaloneItems = [];
        
        // Agrupar elementos por su padre
        const menuItemsByParent = {};
        
        // Inicializar grupos vacíos para cada padre
        parentMenuIds.forEach(parentId => {
          menuItemsByParent[parentId] = [];
        });
        
        // Asignar elementos a sus grupos padre
        this.menuItems.forEach(item => {
          // Si es un elemento con padre y ese padre existe en nuestro conjunto
          if (item.MenuIdPadre && item.MenuIdPadre > 0 && parentMenuIds.has(item.MenuIdPadre)) {
            // Añadir al grupo de su padre
            if (!menuItemsByParent[item.MenuIdPadre]) {
              menuItemsByParent[item.MenuIdPadre] = [];
            }
            menuItemsByParent[item.MenuIdPadre].push(item);
            
            // Guardar referencia al elemento padre si no lo tenemos aún
            if (!parentMenuItems[item.MenuIdPadre]) {
              // Buscar el elemento padre en la lista de menús
              const parentItem = this.menuItems.find(parent => parent.MenuId === item.MenuIdPadre);
              if (parentItem) {
                parentMenuItems[item.MenuIdPadre] = parentItem;
              }
            }
          } 
          // Si es un elemento padre (tiene hijos)
          else if (parentMenuIds.has(item.MenuId)) {
            // Guardar referencia al elemento padre
            parentMenuItems[item.MenuId] = item;
          }
          // Si no es padre ni tiene padre, es un elemento independiente
          else if (!item.MenuIdPadre || item.MenuIdPadre === 0) {
            this.standaloneItems.push(item);
          }
        });
        
        // Crear los grupos finales con título del padre e hijos ordenados
        Object.keys(menuItemsByParent).forEach(parentId => {
          const children = menuItemsByParent[parentId];
          const parentItem = parentMenuItems[parentId];
          
          if (children.length > 0 && parentItem) {
            // Ordenar los hijos por el campo Orden
            const sortedChildren = children.sort((a, b) => {
              const ordenA = parseFloat(a.Orden) || 999;
              const ordenB = parseFloat(b.Orden) || 999;
              return ordenA - ordenB;
            });
            
            this.menuGroups.push({
              parentId: parentId,
              parentTitle: parentItem.Titulo,
              order: parentItem.Orden,
              children: sortedChildren
            });
          }
        });
        
        // Ordenar los grupos por el campo "Orden" del padre
        this.menuGroups.sort((a, b) => {
          const ordenA = parseFloat(a.order) || 999;
          const ordenB = parseFloat(b.order) || 999;
          return ordenA - ordenB;
        });
        
        // Ordenar los elementos independientes por Orden
        this.standaloneItems.sort((a, b) => {
          const ordenA = parseFloat(a.Orden) || 999;
          const ordenB = parseFloat(b.Orden) || 999;
          return ordenA - ordenB;
        });
        
        console.log('Grupos de menú:', this.menuGroups);
        console.log('Elementos independientes:', this.standaloneItems);
      } else {
        console.warn('No se encontró información de menú para el dashboard');
        this.menuItems = [];
        this.menuGroups = [];
        this.standaloneItems = [];
      }
    }
  }
};
</script>



## 3. Actualizar DashboardView.vue - Manejar estados de carga
